import { LogService } from '@trustedshops/tswp-core-common';
import { formatErrorForInstana, serializeError } from '../utils/error-serializer.util';

/**
 * Static helper to check if Instana EUM API (ineum) is available.
 */
function ineumExists(): boolean {
  return (window as any).ineum !== undefined && typeof (window as any).ineum === 'function';
}

/**
 * Creates a window.onerror handler function with shared logic.
 * @param logService Optional LogService for logging (uses console if not provided)
 * @param originalOnError Optional original error handler to chain
 * @param bootstrapperName Name of the bootstrapper for logging
 * @returns The error handler function
 */
export function createWindowOnErrorHandler(
  logService: LogService | undefined,
  originalOnError: ((message: string | Event, source?: string, lineno?: number, colno?: number, error?: Error) => boolean) | undefined,
  bootstrapperName: string
): (message: string | Event, source?: string, lineno?: number, colno?: number, error?: Error) => boolean {
  return (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    try {
      // Create error object if not provided
      const errorObj = error || new Error(
        typeof message === 'string'
          ? message
          : `Error in ${source || 'unknown'}:${lineno || '?'}:${colno || '?'}`
      );

      // Add source location to error if available
      if (source && lineno && colno && !errorObj.stack) {
        errorObj.stack = `at ${source}:${lineno}:${colno}`;
      }

      // Report to Instana (always try this first)
      if (ineumExists()) {
        const formattedError = formatErrorForInstana(errorObj);
        (window as any).ineum('reportError', `[WINDOW-ERROR] ${formattedError}`, {
          source,
          lineno,
          colno,
          message: typeof message === 'string' ? message : String(message)
        });
      }

      // Also log via LogService if available, otherwise use console
      if (logService) {
        logService.error(
          bootstrapperName,
          errorObj,
          { source, lineno, colno, type: 'window.onerror' }
        );
      } else {
        console.error(`[${bootstrapperName}] window.onerror:`, errorObj);
      }
    } catch (e) {
      // Prevent errors in error handler from breaking the app
      console.error(`[${bootstrapperName}] Error in window.onerror handler:`, e);
    }

    // Call original handler if it existed
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }

    // Return false to allow default error handling
    return false;
  };
}

/**
 * Creates an unhandledrejection event handler function with shared logic.
 * @param logService Optional LogService for logging (uses console if not provided)
 * @param bootstrapperName Name of the bootstrapper for logging
 * @returns The event handler function
 */
export function createUnhandledRejectionHandler(
  logService: LogService | undefined,
  bootstrapperName: string
): (event: PromiseRejectionEvent) => void {
  return (event: PromiseRejectionEvent) => {
    try {
      const reason = event.reason;
      let error: Error;

      if (reason instanceof Error) {
        error = reason;
      } else if (reason && typeof reason === 'object') {
        // Try to serialize the error object for better error messages
        let message: string;
        try {
          const serialized = serializeError(reason as any);
          message = JSON.stringify(serialized);
        } catch (serializationError) {
          console.error(
            `[${bootstrapperName}] Failed to serialize unhandledrejection reason:`,
            serializationError,
            'Original reason:',
            reason
          );
          message = String(reason);
        }
        error = new Error(message);
      } else {
        error = new Error(String(reason));
      }

      // Report to Instana (always try this first)
      if (ineumExists()) {
        const formattedError = formatErrorForInstana(error);
        (window as any).ineum('reportError', `[UNHANDLED-REJECTION] ${formattedError}`, {
          reason: reason instanceof Error ? serializeError(reason) : reason,
          type: 'unhandledrejection'
        });
      }

      // Also log via LogService if available, otherwise use console
      if (logService) {
        logService.error(
          bootstrapperName,
          error,
          { reason, type: 'unhandledrejection' }
        );
      } else {
        console.error(`[${bootstrapperName}] unhandledrejection:`, error);
      }
    } catch (e) {
      // Prevent errors in error handler from breaking the app
      console.error(`[${bootstrapperName}] Error in unhandledrejection handler:`, e);
    }
  };
}

/**
 * Sets up global error handlers immediately, before Angular initialization.
 * This ensures error handlers are active before any other code runs.
 *
 * CRITICAL: This should be called at module load time, not during Angular initialization.
 *
 * @param logService Optional LogService for logging (uses console if not provided)
 * @param bootstrapperName Name of the bootstrapper for logging
 */
export function setupErrorHandlersImmediately(
  logService: LogService | undefined,
  bootstrapperName: string
): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Store reference to original handler if it exists
  const originalOnError = window.onerror;

  // Set up handler for unhandled JavaScript errors
  window.onerror = createWindowOnErrorHandler(logService, originalOnError, bootstrapperName);

  // Set up handler for unhandled promise rejections
  window.addEventListener('unhandledrejection', createUnhandledRejectionHandler(logService, bootstrapperName));

  if (logService) {
    logService.information(bootstrapperName, 'Global error handlers (window.onerror, unhandledrejection) registered successfully.');
  } else {
    console.log(`[${bootstrapperName}] Global error handlers (window.onerror, unhandledrejection) registered successfully.`);
  }
}
